// hooks/useCopyGuard.js
import { useEffect, useRef } from "react";

/**
 * useCopyGuard — stop text leaving the page by selection, clipboard, drag, the
 * context menu, or the save / print shortcuts.
 *
 * Two callers: ContentShield (LMS-wide, when the institute blocks copying for
 * students) and ContentViewer (while a "view only, no copying" document is
 * open).
 *
 * The student's OWN typing is never touched — inputs, textareas,
 * contenteditable and the Monaco code editor keep select, copy, cut and paste.
 * Blocking copy of your own draft stops nothing and breaks coding practice
 * (the same rule ExamRunner/useAntiCheat follows).
 *
 * This is deterrence. A browser cannot stop a screenshot or a phone camera;
 * the watermark with the student's name is what makes a leak traceable.
 *
 * @param {object}   opts
 * @param {boolean}  opts.active
 * @param {object}   [opts.scopeRef]  limit selection/clipboard/drag blocking to
 *                                    this element; omit for the whole document
 * @param {Function} [opts.onBlocked] called with the action that was stopped
 *                                    ("copy" | "cut" | "drag" | "menu" | "save" | "print")
 */
const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], .monaco-editor';

export const isEditable = (node) => {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return !!el?.closest?.(EDITABLE);
};

export default function useCopyGuard({ active, scopeRef, onBlocked }) {
  const onBlockedRef = useRef(onBlocked);
  useEffect(() => { onBlockedRef.current = onBlocked; });

  useEffect(() => {
    if (!active) return undefined;
    const scope = () => scopeRef?.current || null;
    const inScope = (node) => {
      const root = scope();
      return !scopeRef || !!(root && node && root.contains(node.nodeType === 1 ? node : node.parentElement));
    };
    // A clipboard event's target is where focus is, not what is selected — so
    // ask the selection whether it touches the protected region.
    const selectionInScope = () => {
      if (!scopeRef) return true;
      const sel = window.getSelection?.();
      return !!sel && (inScope(sel.anchorNode) || inScope(sel.focusNode));
    };
    const blocked = (action) => onBlockedRef.current?.(action);

    const onClipboard = (e) => {
      if (isEditable(e.target) || !selectionInScope()) return;
      e.preventDefault();
      blocked(e.type);
    };
    const onContextMenu = (e) => {
      if (isEditable(e.target) || !inScope(e.target)) return;
      e.preventDefault();
      blocked("menu");
    };
    const onDragStart = (e) => {
      if (isEditable(e.target) || !inScope(e.target)) return;
      e.preventDefault();
      blocked("drag");
    };
    const onSelectStart = (e) => {
      if (isEditable(e.target) || !inScope(e.target)) return;
      e.preventDefault();
    };
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const k = (e.key || "").toLowerCase();
      if (k === "s") { e.preventDefault(); blocked("save"); return; }
      if (k === "p") { e.preventDefault(); blocked("print"); return; }
      // Select-all / copy / cut still work inside the student's own fields.
      if ((k === "a" || k === "c" || k === "x") && !isEditable(e.target)) {
        if (k !== "a" && !selectionInScope()) return;
        e.preventDefault();
        if (k !== "a") blocked(k === "c" ? "copy" : "cut");
      }
    };

    // `user-select: none` does the visible half; the listeners catch what CSS
    // cannot (a stylesheet elsewhere re-enabling selection, keyboard copy).
    const root = scopeRef ? scope() : document.documentElement;
    root?.classList.add("cp-noselect");

    document.addEventListener("copy", onClipboard, true);
    document.addEventListener("cut", onClipboard, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    document.addEventListener("dragstart", onDragStart, true);
    document.addEventListener("selectstart", onSelectStart, true);
    document.addEventListener("keydown", onKeyDown, true);
    // Anything already selected when protection switched on.
    if (selectionInScope()) window.getSelection?.()?.removeAllRanges?.();

    return () => {
      root?.classList.remove("cp-noselect");
      document.removeEventListener("copy", onClipboard, true);
      document.removeEventListener("cut", onClipboard, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
      document.removeEventListener("dragstart", onDragStart, true);
      document.removeEventListener("selectstart", onSelectStart, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [active, scopeRef]);
}
